import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { GerinusService } from '../lib/gerinus.service';
import {
    IDigitalForm,
    IDigitalFormDepartmentUser,
    IDigitalFormDestination,
    IDigitalFormPrepareSignResponse,
    IDigitalFormRepresentado,
    IDigitalFormRequerimentoItem,
    IDigitalFormRequestInfo,
    IDigitalFormSignaturePosition,
} from 'src/app/interfaces/digital-form';

export interface IDigitalFormRequerimentoFilters {
    numeroProcesso?: string;
    interessado?: string;
    assunto?: string;
    dataInicio?: string;
    dataFim?: string;
}

export interface IDigitalFormListResponse {
    data: IDigitalFormRequerimentoItem[];
    meta: { total: number; per_page: number; current_page: number; last_page: number };
}

@Injectable()
export class DigitalFormService extends GerinusService {
    constructor(public http: HttpClient) {
        super(http);
        this.entityName = 'assunto_formulario';
        this.idField = 'AFR_CodigoFormulario';
    }

    private url(path: string): string {
        return `${this.apiURL()}/api/${path}`;
    }

    // ------------------------------------------------------------------
    // Administração
    // ------------------------------------------------------------------

    public getBySubject(assuntoId: number): Observable<{ ok: boolean; formulario: IDigitalForm | null }> {
        return this.http.get<{ ok: boolean; formulario: IDigitalForm | null }>(
            this.url(`subjects/${assuntoId}/digital-form`),
            this.headers(),
        );
    }

    public saveForSubject(
        assuntoId: number,
        payload: Partial<IDigitalForm>,
    ): Observable<{ ok: boolean; message: string; formulario: IDigitalForm }> {
        return this.http.post<{ ok: boolean; message: string; formulario: IDigitalForm }>(
            this.url(`subjects/${assuntoId}/digital-form`),
            payload,
            this.headers(),
        );
    }

    public removeForm(formularioId: number): Observable<{ ok: boolean; message: string }> {
        return this.http.delete<{ ok: boolean; message: string }>(
            this.url(`digital-form/${formularioId}`),
            this.headers(),
        );
    }

    public listDepartmentUsers(
        orgaoId: number,
        departamentoId: number,
    ): Observable<{ ok: boolean; data: IDigitalFormDepartmentUser[] }> {
        return this.http.get<{ ok: boolean; data: IDigitalFormDepartmentUser[] }>(
            this.url(`digital-form/organizations/${orgaoId}/departments/${departamentoId}/users`),
            this.headers(),
        );
    }

    // ------------------------------------------------------------------
    // Cidadão
    // ------------------------------------------------------------------

    /**
     * PDF do requerimento para assinatura no gov.br. A solicitação é gravada depois,
     * com as respostas e o PDF assinado em `formularioDigital`.
     */
    /**
     * Os dados de quem assina (o usuário logado) a API tira do cadastro. Daqui vão só o
     * representado, quando houver, e o telefone informado por quem ainda não tem um.
     */
    public prepareSign(
        assuntoId: number,
        respostas: Record<string, any>,
        representado: IDigitalFormRepresentado | null,
        telefone?: string | null,
    ): Observable<IDigitalFormPrepareSignResponse> {
        return this.http.post<IDigitalFormPrepareSignResponse>(
            this.url(`subjects/${assuntoId}/digital-form/prepare-sign`),
            { respostas, representado, telefone: telefone || null },
            this.headers(),
        );
    }

    // ------------------------------------------------------------------
    // Triagem
    // ------------------------------------------------------------------

    /** Requerimento digital da solicitação; `requerimento` é null quando ela não usa formulário digital. */
    public getForRequest(solicitacaoId: number): Observable<{ ok: boolean; requerimento: IDigitalFormRequestInfo | null }> {
        return this.http.get<{ ok: boolean; requerimento: IDigitalFormRequestInfo | null }>(
            this.url(`digital-form/requests/${solicitacaoId}`),
            this.headers(),
        );
    }

    /** Aprova a solicitação e a envia para a fila de assinatura do responsável. */
    public forwardToSigners(
        solicitacaoId: number,
        nota?: string | null,
    ): Observable<{ ok: boolean; message: string; destino: IDigitalFormDestination }> {
        return this.http.post<{ ok: boolean; message: string; destino: IDigitalFormDestination }>(
            this.url(`digital-form/requests/${solicitacaoId}/forward`),
            { nota: nota || null },
            this.headers(),
        );
    }

    // ------------------------------------------------------------------
    // Responsável
    // ------------------------------------------------------------------

    public listForResponsible(
        page = 1,
        perPage = 10,
        filters: IDigitalFormRequerimentoFilters = {},
    ): Observable<IDigitalFormListResponse> {
        let params = new HttpParams().set('page', String(page)).set('perPage', String(perPage));

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                params = params.set(key, String(value));
            }
        });

        return this.http.get<IDigitalFormListResponse>(this.url('digital-form/responsible'), {
            ...this.headers(),
            params,
        });
    }

    public prepareResponsibleSign(requerimentoId: number): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: IDigitalFormSignaturePosition | null;
    }> {
        return this.http.post<any>(
            this.url(`digital-form/requerimento/${requerimentoId}/prepare-responsible-sign`),
            {},
            this.headers(),
        );
    }

    /** Assina e abre o processo, já tramitado para o destino configurado no formulário. */
    public finalizeResponsibleSign(
        requerimentoId: number,
        fileCloudFlare: string,
        fileName: string,
    ): Observable<{
        ok: boolean;
        message: string;
        processo: { PRC_CodigoProcesso: number; PRC_NumeroProcesso: string };
        destino: IDigitalFormDestination;
    }> {
        return this.http.post<any>(
            this.url(`digital-form/requerimento/${requerimentoId}/finalize-responsible-sign`),
            { fileCloudFlare, fileName },
            this.headers(),
        );
    }

    /** O responsável recusa o requerimento: a solicitação é anulada e o requerente, avisado. */
    public annul(requerimentoId: number, motivo: string): Observable<{ ok: boolean; message: string }> {
        return this.http.post<{ ok: boolean; message: string }>(
            this.url(`digital-form/requerimento/${requerimentoId}/annul`),
            { motivo },
            this.headers(),
        );
    }
}
